import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService, OtpType } from 'src/app/api/auth.service';
import { GoogleAnalyticsService } from 'src/app/services/google-analytics.service';
import { ProfileService } from 'src/app/api/profile.service';
import { ConfirmIpAddressResponse, ConfirmIpAddressResponseKey } from 'src/app/models/user';
import { HCaptchaService } from 'src/app/services/hcaptcha.service';

@Component({
  selector: 'auth-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  Status = Status;
  ConfirmIpAddressResponseKey = ConfirmIpAddressResponseKey;

  returnUrl: string;

  status = Status.Email;
  otpType: OtpType;
  otpTypes: OtpType[] = [];
  loadingCaptcha = false;
  loading = false;
  error = false;
  phoneNumber = '';
  credintials = {
    username: '',
    password: ''
  };

  ipVerificationResponse: ConfirmIpAddressResponse;

  loginForm = this.fb.group({
    email: ['', [Validators.email, Validators.required]],
    password: ['', [Validators.minLength(6), Validators.required]]
  });

  totpForm = this.fb.group({
    code: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]]
  });

  smsForm = this.fb.group({
    code: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]],
    rememberMe: [false]
  });

  constructor(
    private fb: FormBuilder,
    private auth: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private profileService: ProfileService,
    private hCaptchaService: HCaptchaService,
    private ga: GoogleAnalyticsService) {
  }

  ngOnInit() {
    this.checkQueryParams();

    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') || '/dashboard';

    if (this.returnUrl.startsWith('/home')) {
      this.returnUrl = '/dashboard';
    }

    this.ipVerificationResponse = this.profileService.getIpVerificationResponse();
  }

  checkQueryParams() {
    const email = this.route.snapshot.queryParamMap.get('email');

    if (email) {
      this.email.setValue(email);
    }
  }

  onLoginSubmit(): boolean {
    if (this.loginForm.valid) {
      this.credintials = {
        username: this.email.value,
        password: this.password.value
      };

      this.submitLoginRequest();
    }

    return false;
  }

  async submitLoginRequest() {
    let captcha: string = null;

    if (this.hCaptchaService.isCaptchaEnabled) {
      this.loadingCaptcha = true;
      this.hCaptchaService.generatePassiveCaptcha();

      try {
        captcha = await this.hCaptchaService.getCaptcha();
        this.loadingCaptcha = false;
      } catch (err) {
        this.loadingCaptcha = false;
        return;
      }
    }

    this.error = false;
    this.loading = true;
    this.auth.setCredentials(this.credintials.username, this.credintials.password).login(captcha).subscribe((response) => {
      this.loading = false;
      if (response.accessToken) {
        this.login();
      } else {
        if (response.ipVerified) {
          this.otpTypes = response.supportedTypes;

          if (response.supportedTypes.includes(OtpType.TOTP)) {
            this.status = Status.TOTP;
          } else if (response.supportedTypes.includes(OtpType.SMS)) {
            this.sendCode(OtpType.SMS);
          } else {
            this.status = Status.Error;
          }
        } else {
          if (response.verificationSent) {
            this.status = Status.EmailConfirm;
          } else {
            this.error = response.errorDescription;
          }
        }
      }
    }, (err) => {
      this.loading = false;
      this.status = Status.Error;
    });
    return false;
  }

  onTotpSubmit(): boolean {
    this.error = false;
    this.loading = true;
    this.auth.confirmOtpCode(OtpType.TOTP, this.totpCode.value).subscribe((response) => {
      if (response.accessToken) {
        this.login();
      } else {
        this.loading = false;
        this.error = response.errorDescription;
      }
    }, (err) => {
      this.loading = false;
      this.status = Status.Error;
    });

    return false;
  }

  onOtpSubmit(): boolean {
    this.error = false;
    this.loading = true;
    this.auth.confirmOtpCode(this.otpType, this.smsCode.value, this.smsRememberMe.value).subscribe((response) => {
      if (response.accessToken) {
        this.login();
      } else {
        this.loading = false;
        this.error = response.errorDescription;
      }
    }, (err) => {
      this.loading = false;
      this.status = Status.Error;
    });

    return false;
  }

  sendCode(otp: OtpType): boolean {
    this.error = false;
    this.loading = true;
    this.otpType = otp;
    this.auth.sendCode(otp).subscribe((response) => {
      this.loading = false;
      if (response.otpPhone) {
        this.phoneNumber = response.otpPhone;
        this.status = Status.OTP;
      } else {
        this.error = response.errorDescription;
      }
    }, (err) => {
      this.loading = false;
      this.status = Status.Error;
    });

    return false;
  }

  useTotp(): boolean {
    this.status = Status.TOTP;
    return false;
  }

  useSms(): boolean {
    this.sendCode(OtpType.SMS);
    return false;
  }

  useCall(): boolean {
    this.sendCode(OtpType.CALL);
    return false;
  }

  login() {
    this.ga.sendEvent('Account', { action: 'Sign In' });
    this.router.navigateByUrl(this.returnUrl);
  }

  loginAgain() {
    this.status = Status.Email;
  }

  is(s: Status) {
    return !this.loading && this.status === s;
  }

  isCallEnabled() {
    return this.otpTypes.includes(OtpType.CALL);
  }

  isSmsEnabled() {
    return this.otpTypes.includes(OtpType.SMS);
  }

  get email() {
    return this.loginForm.get('email');
  }

  get password() {
    return this.loginForm.get('password');
  }

  get totpCode() {
    return this.totpForm.get('code');
  }

  get smsCode() {
    return this.smsForm.get('code');
  }

  get smsRememberMe() {
    return this.smsForm.get('rememberMe');
  }
}

enum Status {
  Email = 'email',
  EmailConfirm = 'emailConfirm',
  TOTP = 'totp',
  OTP = 'otp',
  Error = 'error'
}
